import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, Input, OnInit, Self
} from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, NgControl, UntypedFormBuilder, UntypedFormGroup,
  ValidationErrors, Validator, Validators
} from '@angular/forms';
import { skip, takeUntil } from 'rxjs/operators';

import { EntityTypeDetails } from 'generated/graphql';

import { FormGroupConfig } from 'core/form-config-type';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';

export interface NameFormModel {
  firstName: string;
  middleName?: string;
  lastName?: string;
  nickname?: string;
  suffix?: string;
  pronouns?: string;
  pronounsSelect?: string;
  customPronouns?: string;
}

@Component({
  selector: 'app-name-form',
  templateUrl: './name-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NameFormComponent
  extends WithDisposableSubject()
  implements ControlValueAccessor, OnInit, DoCheck, Validator {
  formGroup: UntypedFormGroup;

  @Input() showPronounsOnLoad = false;
  @Input() entityType: EntityTypeDetails.Fragment;

  addMiddleNameButtonVisible = false;
  addNicknameButtonVisible = false;
  addSuffixButtonVisible = false;
  addPronounsButtonVisible = false;

  private isTouched = false;

  _onTouched = () => { };
  _onChanged = (value: NameFormModel) => { };

  constructor(
    private _fb: UntypedFormBuilder,
    private _cd: ChangeDetectorRef,
    @Self() public controlDir: NgControl
  ) {
    super();
    controlDir.valueAccessor = this;

    const config: FormGroupConfig<NameFormModel> = {
      firstName: [null, { validators: [Validators.required] }],
      nickname: [null],
      middleName: [null],
      lastName: [null],
      suffix: [null],
      pronouns: [null],
      pronounsSelect: [null],
      customPronouns: [{ value: null, disabled: true }]
    };

    const fg = this._fb.group(config);

    this.formGroup = fg;
  }

  get customPronounsVisible() {
    return this.formGroup?.get('pronounsSelect')?.value === 'custom';
  }

  // https://stackblitz.com/edit/angular-eqntdj?file=src%2Fapp%2Fmy-custom-control.component.ts
  ngOnInit() {
    // NOTE: Have to bind to `this` to set the validator for the incoming NgControl
    this.controlDir.control.setValidators(this.validate.bind(this));
    // this.formGroup.patchValue({});
    this.addPronounsButtonVisible = this.showPronounsOnLoad;

    this.watchProunounsValues();
  }

  ngDoCheck() {
    if (this.controlDir.touched && !this.isTouched) {
      this.isTouched = true;
      this.formGroup.markAllAsTouched();
    }
  }

  watchProunounsValues() {
    const customPronounsCtrl = this.formGroup.get('customPronouns');
    const pronounsSelectCtrl = this.formGroup.get('pronounsSelect');
    const pronounsControl = this.formGroup.get('pronouns');

    pronounsSelectCtrl.valueChanges.subscribe(value => {
      if (value === 'custom') {
        customPronounsCtrl.enable();
        customPronounsCtrl.valueChanges.pipe(
          takeUntil(pronounsSelectCtrl.valueChanges)
        ).subscribe({
          next(cpValue) {
            pronounsControl.setValue(cpValue);
          },
          complete() {
            console.log('disposed');
          }
        });
      } else {
        pronounsControl.setValue(value);
        customPronounsCtrl.disable();
      }
    });
  }

  /** Control Value Accessor Implementmenation */
  writeValue(obj: NameFormModel): void {
    if (obj) {
      if (obj.pronouns) {
        const pronouns = obj.pronouns;
        if (pronouns !== 'he/him' && pronouns !== 'she/her' && pronouns !== 'they/them') {
          obj.customPronouns = obj.pronouns;
          obj.pronounsSelect = 'custom';
        } else {
          obj.pronounsSelect = pronouns;
        }
      }
      this.formGroup.patchValue(obj, { emitEvent: false });
      this._cd.markForCheck();
    }
  }

  registerOnChange(fn: any): void {
    this._onChanged = fn;
    this.formGroup.valueChanges.pipe(
      takeUntil(this.disposer),
    ).subscribe(() => {
      this._onChanged(this.getValue());
    });
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }

  /** Validator Implementation */
  validate(control: AbstractControl): ValidationErrors {
    if (this.formGroup.valid) {
      return null;
    }
    return { basicInfo: true };
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.formGroup.valueChanges.pipe(
      skip(1),
      takeUntil(this.disposer)
    ).subscribe((_) => {
      fn();
    });
  }

  getValue(): NameFormModel {
    const fv = this.formGroup.value;
    return {
      firstName: fv.firstName,
      lastName: fv.lastName,
      nickname: fv.nickname,
      middleName: fv.middleName,
      suffix: fv.suffix,
      pronouns: fv.pronounsSelect === 'custom' ? fv.customPronouns : fv.pronounsSelect
    };
  }
}
